import {FC, RefObject, useMemo, useState} from 'react';
import {Section} from '@components/Section';
import {Accordion, Col, Row, Stack} from 'react-bootstrap';
import {Typography} from '@components/Typography';
import {AdvertTitle} from '@liveComponents/AdvertTitle';
import {PropertyAttributes} from '@components/PropertyAttributes';
import {StickyBoxContent, StickyBoxContentProps} from './StickyBoxContent';
import {Divider} from '@components/Divider';
import {Box} from '@components/Box';
import {ContentBox} from '@components/ContentBox';
import {FormattedMessage, useIntl} from 'react-intl';
import {StickyBox} from '@components/StickyBox';
import {NeighborhoodMap} from '@components/NeighborhoodMap';
import {IntersectionLoadable} from '@liveComponents/Loadable';
import {Container} from '@components/Container';
import dynamic from 'next/dynamic';
import {BasicParameters, BasicParametersProps} from './BasicParameters';
import {AdditionalParameters, AdditionalParametersProps} from './AdditionalParameters';
import {AdvertImportType, Disposition, EstateType, Locale, OfferType, Project, Scalars} from '.cache/__types__';
import {PoiMapProps} from '@pageComponents/Detail/PoiMap';
import {Mortgage} from '@pageComponents/Detail/Mortgage';
import {Button} from '@components/Button';
import {EChalupyParameters} from '@pageComponents/Detail/EChalupyParameters';
import {Tag} from '@components/Tag';
import {TagList} from '@components/TagList';
import {DetailO2} from '@liveComponents/Banner/DetailO2';
import {DetailCEZ} from '@liveComponents/Banner/DetailCEZ';
import {DetailUPC} from '@liveComponents/Banner/DetailUPC';
import {LocaleEnum} from 'src/types/general';
import {useLocale} from '@utils/locale';
import {useAdvertDetail} from "@pageComponents/Detail/context";

const PoiMap = dynamic(() => import('@pageComponents/Detail/PoiMap'), {ssr: false});

export type ContentProps = {
    advert: {
        type: AdvertImportType;
        dataJson?: string;
        description: string;
        descriptionEnglish: string;
        address: string;
        tags: string[];
        poiData: string;
        offerType: OfferType;
        estateType: EstateType;
        disposition: Disposition;
        surface: number;
        locale: Locale;
        roommate?: boolean;
        reserved?: boolean;
        project?: Project;
        mortgageData?: {
            rate: number;
        };
        regionTree: {
            id: Scalars['ID'];
            name: string;
            uri: string;
        }[];
    };
    sendMessageButtonRef?: RefObject<HTMLButtonElement>;
    isRk?: boolean;
} & BasicParametersProps &
    AdditionalParametersProps &
    StickyBoxContentProps &
    PoiMapProps;

export const Content: FC<ContentProps> = ({advert, isRk, onContactOwnerModalOpen, sendMessageButtonRef}) => {
    const {banner} = useAdvertDetail();

    const description = useMemo(
        () =>
            advert.description.split('\n').map((paragraph, idx) => (
                <Typography key={idx} as="p" color={idx > 0 ? 'grey-dark' : undefined} variant={idx === 0 ? 'perex-lg' : 'perex'}>
                    {paragraph}
                </Typography>
            )),
        [advert.description],
    );

    const englishDescription = useMemo(
        () =>
            advert.descriptionEnglish?.split('\n').map((paragraph, idx) => (
                <Typography key={idx} as="p" color={idx > 0 ? 'grey-dark' : undefined} variant={idx === 0 ? 'perex-lg' : 'perex'}>
                    {paragraph}
                </Typography>
            )),
        [advert.descriptionEnglish],
    );

    const intl = useIntl();
    const mapHash = useMemo(() => intl.formatMessage({defaultMessage: 'mapa'}), [intl]);

    return (
        <Container variant="narrow">
            <Section>
                <Row className="justify-content-between">
                    <Col lg={10} md={9} xl={9} xxl={8}>
                        <Typography as="h1" className="mb-3 mb-lg-10" variant="h2">
                            <AdvertTitle
                                offerType={advert?.offerType}
                                estateType={advert?.estateType}
                                disposition={advert?.disposition}
                                surface={advert?.surface}
                                isRk={isRk}
                            />
                        </Typography>

                        <TagList>
                            {advert.roommate && (
                                <Tag variant="green">
                                    <FormattedMessage defaultMessage="Vhodné ke spolubydlení" />
                                </Tag>
                            )}

                            {advert.reserved && (
                                <Tag variant="dark">
                                    <FormattedMessage defaultMessage="Rezervováno" />
                                </Tag>
                            )}

                            {advert.project && (
                                <Tag variant="outline-medium">
                                    <FormattedMessage defaultMessage="Developerský projekt" />
                                </Tag>
                            )}
                        </TagList>

                        <PropertyAttributes>
                            <PropertyAttributes.Item iconName="Location">
                                <a href={'#' + mapHash}>{advert.address}</a>
                            </PropertyAttributes.Item>
                            <PropertyAttributes.Item iconName="Equipment">{advert.tags.join(' • ')}</PropertyAttributes.Item>
                        </PropertyAttributes>

                        <StickyBoxContent advert={advert} className="mt-6 d-md-none" onContactOwnerModalOpen={onContactOwnerModalOpen} />
                        <Divider className="mt-8 mb-8 mt-lg-20 mb-lg-20" />
                        <Box className="mb-8">{description}</Box>

                        {advert.descriptionEnglish && advert.descriptionEnglish !== '' && (
                            <Accordion flush>
                                <Accordion.Item eventKey="0">
                                    <Accordion.Button as={Button} variant="link" className="mb-8">
                                        <FormattedMessage defaultMessage="Zobrazit anglický popis" />
                                    </Accordion.Button>
                                    <Accordion.Body>
                                        <Box className="mb-8">{englishDescription}</Box>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        )}

                        <ContentBox className="px-5 py-6 px-xl-8 py-xl-10 mb-8 mb-xl-20" outline={true}>
                            <Stack className="mb-last-0">
                                {advert.type !== AdvertImportType.EChalupy && <BasicParameters advert={advert} />}
                                {/*}
                                        <Box className="mb-6 mb-lg-10">
                                            <div
                                                style={{
                                                    maxWidth: '40rem',
                                                }}
                                            >
                                                <PromoCard iconName="Percentage" variant="white">
                                                    <PromoCard.Title>Výhodná nabídka.</PromoCard.Title>
                                                    <PromoCard.Desc>
                                                        Cena této nabídky je nižší než obvyklý průměr <strong>18 000
                                                        Kč</strong> z této
                                                        lokality. <a href="#">Zobrazit podobné nabídky</a>
                                                    </PromoCard.Desc>
                                                </PromoCard>
                                            </div>
                                        </Box>
                                        {*/}
                                {advert.type !== AdvertImportType.EChalupy && <AdditionalParameters advert={advert} />}

                                {advert.type === AdvertImportType.EChalupy && advert.dataJson && (
                                    <EChalupyParameters data={advert.dataJson} />
                                )}
                            </Stack>
                        </ContentBox>

                        {advert.mortgageData && advert.mortgageData.rate && (
                            <Mortgage price={advert.price} currency={advert.currency} rate={advert.mortgageData.rate / 100} />
                        )}
                        <Box className="py-6 py-xl-10">
                            {banner === 'cez' && <DetailCEZ />}
                            {banner === 'o2' && <DetailO2 />}
                            {banner === 'upc' && <DetailUPC />}
                        </Box>
                    </Col>
                    <Col lg={6} md={7} xxl={5}>
                        <StickyBox>
                            <StickyBoxContent
                                sendMessageButtonRef={sendMessageButtonRef}
                                advert={advert}
                                className="d-none d-md-block"
                                onContactOwnerModalOpen={onContactOwnerModalOpen}
                            />
                        </StickyBox>
                    </Col>
                </Row>
            </Section>
            <Section id={mapHash}>
                <Typography as="h2" className="mb-6" variant="h3">
                    <FormattedMessage defaultMessage="V okolí nemovitosti najdete" />
                </Typography>
                <NeighborhoodMap>
                    <IntersectionLoadable rootMargin="300px" keepRendered>
                        <PoiMap advert={advert} />
                    </IntersectionLoadable>
                </NeighborhoodMap>
            </Section>
        </Container>
    );
};
